import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#374151')};
  margin-bottom: 8px;
`;

const SelectContainer = styled.div`
  position: relative;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 12px 14px;
  padding-right: 40px;
  font-size: 14px;
  color: ${(props) =>
    props.hasValue
      ? props.theme.name === 'dark'
        ? '#F3F4F6'
        : '#1F2937'
      : props.theme.name === 'dark'
      ? '#6B7280'
      : '#9CA3AF'};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.isOpen
        ? '#FA4616'
        : props.theme.name === 'dark'
        ? '#374151'
        : '#D1D5DB'};
  border-radius: 8px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:focus {
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ChevronIcon = styled.div`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%)
    ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  pointer-events: none;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1F2937' : '#ffffff'};
  border: 1px solid
    ${(props) => (props.theme.name === 'dark' ? '#374151' : '#E5E7EB')};
  border-radius: 8px;
  box-shadow: ${(props) =>
    props.theme.name === 'dark'
      ? '0 10px 15px -3px rgba(0, 0, 0, 0.3)'
      : '0 10px 15px -3px rgba(0, 0, 0, 0.1)'};
  z-index: 9999;
  max-height: 280px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SearchInputWrapper = styled.div`
  padding: 8px;
  border-bottom: 1px solid
    ${(props) => (props.theme.name === 'dark' ? '#374151' : '#E5E7EB')};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB'};
  border: 1px solid
    ${(props) => (props.theme.name === 'dark' ? '#374151' : '#E5E7EB')};
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #fa4616;
  }

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#6B7280' : '#9CA3AF')};
  }
`;

const OptionsList = styled.div`
  overflow-y: auto;
  max-height: 200px;
  padding: 4px;
`;

const Option = styled.button`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: ${(props) =>
    props.isSelected
      ? '#FA4616'
      : props.theme.name === 'dark'
      ? '#F3F4F6'
      : '#1F2937'};
  background: ${(props) =>
    props.isSelected
      ? props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.1)'
        : 'rgba(250, 70, 22, 0.05)'
      : 'transparent'};
  border: none;
  border-radius: 6px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)'};
  }
`;

const CreateOption = styled(Option)`
  color: #fa4616;
  font-weight: 500;
  border-top: 1px solid
    ${(props) => (props.theme.name === 'dark' ? '#374151' : '#E5E7EB')};
  border-radius: 0 0 6px 6px;
  margin-top: 4px;
  padding-top: 12px;

  svg {
    margin-right: 8px;
  }
`;

const NoResults = styled.div`
  padding: 16px;
  text-align: center;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  font-size: 14px;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid
    ${(props) => (props.theme.name === 'dark' ? '#374151' : '#E5E7EB')};
  border-top-color: #fa4616;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ErrorMessage = styled.span`
  display: block;
  color: #ef4444;
  font-size: 12px;
  margin-top: 6px;
`;

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 2a.75.75 0 01.75.75v4.5h4.5a.75.75 0 010 1.5h-4.5v4.5a.75.75 0 01-1.5 0v-4.5h-4.5a.75.75 0 010-1.5h4.5v-4.5A.75.75 0 018 2z" />
  </svg>
);

const CreatableSelect = ({
  options = [],
  value,
  onChange,
  onCreateOption,
  placeholder = 'Select an option...',
  label,
  disabled = false,
  loading = false,
  error,
  searchable = true,
  createLabel = 'Create',
  noOptionsMessage = 'No options found',
  formatCreateLabel,
  getOptionLabel = (option) => option.label,
  getOptionValue = (option) => option.value,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const filteredOptions = options.filter((option) =>
    getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const showCreateOption =
    searchTerm.trim() &&
    !filteredOptions.some(
      (option) =>
        getOptionLabel(option).toLowerCase() === searchTerm.toLowerCase()
    ) &&
    onCreateOption;

  const handleSelect = useCallback(
    (option) => {
      onChange(option);
      setIsOpen(false);
      setSearchTerm('');
    },
    [onChange]
  );

  const handleCreate = useCallback(async () => {
    if (!onCreateOption || !searchTerm.trim()) return;

    setIsCreating(true);
    try {
      const newOption = await onCreateOption(searchTerm.trim());
      if (newOption) {
        onChange(newOption);
      }
      setIsOpen(false);
      setSearchTerm('');
    } catch (err) {
      // Handle error silently or pass to parent
    } finally {
      setIsCreating(false);
    }
  }, [onCreateOption, searchTerm, onChange]);

  const selectedLabel = value ? getOptionLabel(value) : null;

  const getCreateLabelText = () => {
    if (formatCreateLabel) {
      return formatCreateLabel(searchTerm);
    }
    return `${createLabel} "${searchTerm}"`;
  };

  return (
    <Container ref={containerRef}>
      {label && <Label>{label}</Label>}
      <SelectContainer>
        <SelectButton
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          isOpen={isOpen}
          hasValue={!!value}
        >
          <span>{selectedLabel || placeholder}</span>
        </SelectButton>
        <ChevronIcon isOpen={isOpen}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.22 6.22a.75.75 0 011.06 0L8 8.94l2.72-2.72a.75.75 0 111.06 1.06l-3.25 3.25a.75.75 0 01-1.06 0L4.22 7.28a.75.75 0 010-1.06z" />
          </svg>
        </ChevronIcon>

        {isOpen && (
          <Dropdown>
            {searchable && (
              <SearchInputWrapper>
                <SearchInput
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search or create..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </SearchInputWrapper>
            )}

            {loading ? (
              <LoadingContainer>
                <LoadingSpinner />
              </LoadingContainer>
            ) : (
              <OptionsList>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => {
                    const optionValue = getOptionValue(option);
                    const isSelected =
                      value && getOptionValue(value) === optionValue;

                    return (
                      <Option
                        key={optionValue}
                        type="button"
                        isSelected={isSelected}
                        onClick={() => handleSelect(option)}
                      >
                        <span>{getOptionLabel(option)}</span>
                        {isSelected && <CheckIcon />}
                      </Option>
                    );
                  })
                ) : !showCreateOption ? (
                  <NoResults>{noOptionsMessage}</NoResults>
                ) : null}

                {showCreateOption && (
                  <CreateOption
                    type="button"
                    onClick={handleCreate}
                    disabled={isCreating}
                  >
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {isCreating ? (
                        <LoadingSpinner style={{ marginRight: 8 }} />
                      ) : (
                        <PlusIcon />
                      )}
                      {getCreateLabelText()}
                    </span>
                  </CreateOption>
                )}
              </OptionsList>
            )}
          </Dropdown>
        )}
      </SelectContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
};

CreatableSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onCreateOption: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.string,
  searchable: PropTypes.bool,
  createLabel: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  formatCreateLabel: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
};

export default CreatableSelect;
